import { memo, useCallback, useEffect, useMemo, useState } from "react";
import useValidate from "../../store/hooks/useValidator";
import CreateUi from "./CreateUi";
import { useDispatch } from "react-redux";
import { callApiAction } from "../../store/actions/commonAction";
import { closeModal } from "../../store/actions/modalAction";
import {
  addEventApi,
  getEventByIdApi,
  updateEventApi,
} from "../../apis/event.api";
import moment from "moment";
import { SNACK_BAR_VARIETNS } from "../../utils/constants";
import { callSnackBar } from "../../store/actions/snackbarAction";

const CreateController = ({ callBack = () => {}, id, type }) => {
  const validate = useValidate();
  const dispatch = useDispatch();

  const title = "Event ";
  const modalKey = "event";

  const createApi = addEventApi;
  const getByIdApi = getEventByIdApi;
  const updateApi = updateEventApi;

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fields, setFields] = useState({
    err: "",
    id,
    name: "",
    description: "",
    date: moment().toISOString(),
    logo: null,
    venue: "",
    country: null,
    state: null,
    district: null,
    taluka: null,
    donors: [],
    ngos: [],
    noOfFollowUps: 0,
    followUpsDuration: [],
  });
  const [originalDocument, setOriginalDocument] = useState({});

  const serializeFollowUpsDuration = (durations = []) => {
    return durations.map((duration) => Number(duration));
  };

  const validationSchemaForCreate = useMemo(
    () => [
      {
        required: true,
        value: fields.name,
        field: "Event Name",
      },
      {
        required: true,
        value: fields.description,
        field: "Event Description",
      },

      {
        required: true,
        value: fields.date,
        field: " Date",
      },

      {
        required: true,
        value: fields.venue,
        field: "Event Venue",
      },
      {
        required: true,
        value: fields.country,
        field: "Country",
      },
      {
        required: true,
        value: fields.state,
        field: "State",
      },
      {
        required: true,
        value: fields.district,
        field: "District",
      },
      {
        required: true,
        value: fields.taluka,
        field: "Taluka",
      },

      {
        required: true,
        value: fields.donors,
        custom: () => {
          if (
            fields.donors &&
            Array.isArray(fields.donors) &&
            fields.donors.length > 0
          ) {
            return true;
          }
          return "Please Select Donors";
        },
      },

      {
        required: true,
        value: fields.ngos,
        custom: () => {
          if (
            fields.ngos &&
            Array.isArray(fields.ngos) &&
            fields.ngos.length > 0
          ) {
            return true;
          }
          return "Please Select NGOs";
        },
      },

      {
        required: true,
        value: fields.logo,
        field: "Event Logo",
      },
      {
        required: true,
        value: fields.noOfFollowUps,
        custom: () => {
          const noOfFollowUps = Number(fields.noOfFollowUps);

          if (Number.isInteger(noOfFollowUps) && noOfFollowUps > 0) {
            return true;
          }

          return "Please enter a valid follow-up count";
        },
      },
      {
        required: true,
        value: fields.followUpsDuration,
        custom: () => {
          const noOfFollowUps = Number(fields.noOfFollowUps);

          if (!Number.isInteger(noOfFollowUps) || noOfFollowUps <= 0) {
            return "Please enter a valid follow-up count";
          }

          if (
            !Array.isArray(fields.followUpsDuration) ||
            fields.followUpsDuration.length !== noOfFollowUps
          ) {
            return "Please enter duration for each follow-up";
          }

          const hasInvalidDuration = fields.followUpsDuration.some(
            (duration) =>
              duration === "" ||
              duration === null ||
              duration === undefined ||
              Number.isNaN(Number(duration)),
          );

          if (hasInvalidDuration) {
            return "Please enter duration for each follow-up";
          }

          return true;
        },
      },
    ],
    [fields],
  );

  const validationSchemaForUpdate = useMemo(
    () => [
      {
        required: true,
        value: fields.name,
        field: "Event Name",
      },
      {
        required: true,
        value: fields.description,
        field: "Event Description",
      },

      {
        required: true,
        value: fields.date,
        field: " Date",
      },

      {
        required: true,
        value: fields.venue,
        field: "Event Venue",
      },

      {
        required: true,
        value: fields.logo,
        field: "Event Logo",
      },

      {
        required: true,
        value: fields.country,
        field: "Country",
      },
      {
        required: true,
        value: fields.state,
        field: "State",
      },
      {
        required: true,
        value: fields.district,
        field: "District",
      },
      {
        required: true,
        value: fields.taluka,
        field: "Taluka",
      },
      {
        required: true,
        value: fields.noOfFollowUps,
        custom: () => {
          const noOfFollowUps = Number(fields.noOfFollowUps);

          if (Number.isInteger(noOfFollowUps) && noOfFollowUps > 0) {
            return true;
          }

          return "Please enter a valid follow-up count";
        },
      },
      {
        required: true,
        value: fields.followUpsDuration,
        custom: () => {
          const noOfFollowUps = Number(fields.noOfFollowUps);

          if (!Number.isInteger(noOfFollowUps) || noOfFollowUps <= 0) {
            return "Please enter a valid follow-up count";
          }

          if (
            !Array.isArray(fields.followUpsDuration) ||
            fields.followUpsDuration.length !== noOfFollowUps
          ) {
            return "Please enter duration for each follow-up";
          }

          const hasInvalidDuration = fields.followUpsDuration.some(
            (duration) =>
              duration === "" ||
              duration === null ||
              duration === undefined ||
              Number.isNaN(Number(duration)),
          );

          if (hasInvalidDuration) {
            return "Please enter duration for each follow-up";
          }

          return true;
        },
      },
    ],
    [fields],
  );

  const createFunction = async () => {
    const validationResponse = validate(validationSchemaForCreate);

    if (validationResponse === true) {
      const payload = {
        ...fields,
        noOfFollowUps: Number(fields.noOfFollowUps),
        followUpsDuration: serializeFollowUpsDuration(fields.followUpsDuration),
      };

      setLoading(true);
      dispatch(
        callApiAction(
          async () => await createApi(payload),
          async (response) => {
            await callBack(response);
            setLoading(false);
            dispatch(closeModal(modalKey));
            dispatch(
              callSnackBar("Create Successfully", SNACK_BAR_VARIETNS.suceess),
            );
          },
          (err) => {
            setLoading(false);
            setFields({ ...fields, err });
          },
        ),
      );
    } else {
      setFields({ ...fields, err: validationResponse });
    }
  };

  const updateFunction = async () => {
    let updatedData = { id };
    const validationResponse = validate(validationSchemaForUpdate);

    if (validationResponse === true) {
      for (let field in originalDocument) {
        if (fields[field] !== originalDocument[field]) {
          const fieldVal = fields[field];

          if (Array.isArray(fieldVal)) {
            updatedData[field] = serializeFollowUpsDuration(fieldVal);
          } else if (fieldVal && typeof fieldVal == "object") {
            updatedData[field] = fieldVal["_id"];
          } else {
            updatedData[field] = fieldVal;
          }
        }
      }

      setLoading(true);
      dispatch(
        callApiAction(
          async () => await updateApi(updatedData),
          async (response) => {
            await callBack(response, updatedData);
            setLoading(false);
            dispatch(
              callSnackBar(
                "Data Updated Successfully",
                SNACK_BAR_VARIETNS.suceess,
              ),
            );
            dispatch(closeModal(modalKey));
          },
          (err) => {
            setLoading(false);
            setFields({ ...fields, err });
          },
        ),
      );
    } else {
      setFields({ ...fields, err: validationResponse });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (id) updateFunction();
    else createFunction();
  };

  const fetchById = useCallback(
    (id) => {
      setFetchLoading(true);
      dispatch(
        callApiAction(
          async () => await getByIdApi({ id, forUpdate: true }),
          async (response) => {
            setFields((currentFields) => ({
              ...currentFields,
              ...response,
            }));
            setOriginalDocument(response);
            setFetchLoading(false);
          },
          (err) => {
            setFields((currentFields) => ({
              ...currentFields,
              err,
            }));
            setFetchLoading(false);
          },
        ),
      );
    },
    [dispatch, getByIdApi],
  );

  useEffect(() => {
    if (id) fetchById(id);
  }, [fetchById, id]);

  return (
    <CreateUi
      fetchLoading={fetchLoading}
      modalKey={modalKey}
      title={title}
      isUpdate={id}
      loading={loading}
      fields={fields}
      onSubmit={onSubmit}
      setFields={setFields}
    />
  );
};
export default memo(CreateController);
